import { Box, Stack, Typography, styled, Tabs } from "@mui/material";

export const ContentContainer = styled(Stack)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: "2rem",
  color: theme.customColors.textPrimary
}));

export const HeaderStyled = styled(Box)(({ theme }) => ({
  display: "flex",
  flexShrink: 0,
  flexWrap: "wrap",
  gap: theme.spacing(1.5),
  justifyContent: "space-between",
  alignItems: "start",
  width: "calc(100% - 2rem)",
  "& #close-modal-icon": {
    cursor: "pointer",
    "aspect-ratio": "1 / 1",
    flexShrink: 0
  }
}));

export const GenericContentContainer = styled(ContentContainer)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(3),
  maxHeight: "calc(100vh - 4rem)",
  overflow: "hidden",
  width: "100%",
  "& #progressbar-container": {
    flexShrink: 0
  },
  [theme.breakpoints.down("sm")]: {
    gap: theme.spacing(2)
  }
}));

export const HeaderTitleGroup = styled(Box)(() => ({
  display: "flex",
  flexDirection: "column",
  gap: "0.5rem",
  minWidth: 0
}));

export const CloseModalButton = styled("button")(({ theme }) => ({
  alignItems: "center",
  aspectRatio: "1 / 1",
  background: "transparent",
  border: "none",
  color: "inherit",
  cursor: "pointer",
  display: "inline-flex",
  flexShrink: 0,
  justifyContent: "center",
  padding: 0,
  position: "absolute",
  top: "2rem",
  right:"2rem",
  "&:focus-visible": {
    outline: `2px solid ${theme.customColors.textPrimary}`,
    outlineOffset: "2px"
  },
  [theme.breakpoints.down("sm")]:{
    top: "1rem",
    right: "0.75rem"
  }
}));

export const SubHeaderText = styled(Typography)(({ theme }) => ({
  color: theme.customColors.textDisabledAlternative,
  fontSize: "0.75rem",
  fontWeight: 400,
  lineHeight: "0.9375rem"
}));

export const DescriptionText = styled(Typography)(({ theme }) => ({
  color: theme.customColors.exploreCardText,
  flexShrink: 0,
  fontSize: "1rem",
  fontWeight: 400,
  lineHeight: 1.5,
  whiteSpace: "pre-wrap",
  [theme.breakpoints.down("sm")]: {
    fontSize: "0.875rem"
  }
}));

export const ActionsRow = styled(Stack)(({ theme }) => ({
  alignItems: "center",
  flexDirection: "row",
  flexShrink: 0,
  flexWrap: "wrap",
  gap: theme.spacing(2),
  justifyContent: "space-between",
  position: "relative",
  width: "100%",
  "& #cta-overlay-toggle": {
    bottom: "unset !important",
    position: "static !important",
    right: "unset !important"
  },
  [theme.breakpoints.up("sm")]: {
    "& #cta-overlay": {
      bottom: "unset !important",
      position: "static !important",
      right: "unset !important"
    }
  },
  [theme.breakpoints.down("sm")]: {
    "& #cta-overlay": {
      bottom: "calc(100% + 0.5rem) !important",
      right: "0 !important"
    }
  }
}));

export const StartActionsGroup = styled(Box)(({ theme }) => ({
  alignItems: "center",
  display: "flex",
  flexDirection: "row",
  gap: theme.spacing(0.5)
}));

export const TabsContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  flex: "1 1 auto",
  flexDirection: "column",
  minHeight: 0,
  overflow: "hidden",
  width: "100%",
  "& [role='tabpanel']": {
    flex: "1 1 auto",
    minHeight: 0,
    overflowY: "auto",
    overscrollBehavior: "contain",
    padding: `${theme.spacing(3)} 0 0 0`
  }
}));

export const TabBody = styled(Box)(() => ({
  width: "100%"
}));

export const EmptyStateText = styled(Typography)(({ theme }) => ({
  color: theme.customColors.exploreCardText,
  fontSize: "0.875rem",
  fontWeight: 400,
  padding: theme.spacing(2, 0),
  textAlign: "start",
  width: "100%"
}));

export const TeachersGrid = styled(Box)(({ theme }) => ({
  display: "grid",
  gap: theme.spacing(2),
  gridTemplateColumns: "repeat(4, 1fr)",
  width: "100%",
  [theme.breakpoints.down("md")]: {
    gridTemplateColumns: "repeat(3, 1fr)"
  },
  [theme.breakpoints.down("sm")]: {
    gridTemplateColumns: "1fr"
  }
}));

export const TeachersSectionTitle = styled(Typography)(({ theme }) => ({
  color: theme.customColors.textPrimary,
  fontSize: "1.125rem",
  fontWeight: 600,
  paddingBottom: theme.spacing(1)
}));

export const TeachersSectionDescription = styled(Typography)(({ theme }) => ({
  color: theme.customColors.exploreCardText,
  fontSize: "0.875rem",
  fontWeight: 400,
  paddingBottom: theme.spacing(2)
}));

export const TeacherCardBox = styled(Box)(({ theme }) => ({
  alignItems: "center",
  display: "flex",
  flexDirection: "row",
  gap: theme.spacing(1.5),
  width: "100%"
}));

export const TeacherImageBox = styled(Box)(({ theme }) => ({
  alignItems: "center",
  backgroundColor: theme.customColors.backgroundDisabled,
  borderRadius: theme.spacing(1),
  display: "flex",
  flexShrink: 0,
  height: "3.5rem",
  justifyContent: "center",
  overflow: "hidden",
  width: "3.5rem",
  "& img": {
    height: "100%",
    objectFit: "cover",
    width: "100%"
  }
}));

export const TeacherInitials = styled(Typography)(({ theme }) => ({
  color: theme.customColors.textInput,
  fontSize: "1rem",
  fontWeight: 700
}));

export const TeacherInfo = styled(Box)(() => ({
  display: "flex",
  flexDirection: "column",
  gap: "0.125rem",
  minWidth: 0
}));

export const TeacherName = styled(Typography)(({ theme }) => ({
  color: theme.customColors.textPrimary,
  fontSize: "1rem",
  fontWeight: 600,
  lineHeight: 1.25,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap"
}));

export const TeacherRoleRow = styled(Box)(({ theme }) => ({
  alignItems: "center",
  color: theme.customColors.exploreCardText,
  display: "flex",
  flexDirection: "row",
  gap: theme.spacing(0.5)
}));

export const TeacherRoleText = styled(Typography)(({ theme }) => ({
  color: theme.customColors.exploreCardText,
  fontSize: "0.875rem",
  fontWeight: 400
}));

export const LoadingWrapper = styled(Box)(({ theme }) => ({
  alignItems: "center",
  display: "flex",
  justifyContent: "center",
  minHeight: "200px",
  padding: theme.spacing(4),
  width: "100%"
}));

export const ContentTabs = styled(Tabs)(({ theme }) => ({
  display: "flex",
  flexDirection: "row",
  maxWidth: "100%",
  padding: "1rem 0rem",
  "> .Mui-disabled": {
    display: "none"
  },
  fontWeight: "500",
  ".MuiTab-textColorPrimary:not(.Mui-selected)": { color: theme.customColors.systemPrimary01 },
  ".Mui-selected": { color: theme.customColors.accentVariantA },
  fontFamily: "Inter"
}));
